import React, { useState } from 'react';
import useStore from '../store/useStore';
import { format } from 'date-fns';
import './DynamicEntityTable.css';

const DynamicEntityTable = ({ entities, entityType, canEdit }) => {
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const { latestSignals, updateEntity, deleteEntity, entitiesWithYTD } = useStore();

  if (!entities || entities.length === 0) {
    return (
      <div className="empty-state">
        <p>No {entityType?.toLowerCase() || 'entities'} defined.</p>
        <p>Click "Add Entity" to create one.</p>
      </div>
    );
  }

  const handleEdit = (entity) => {
    setEditingId(entity.Id);
    setEditData({
      Title: entity.Title || '',
      EntityType: entity.EntityType || '',
      TargetValue: entity.TargetValue || '',
      TargetUnit: entity.TargetUnit || '',
      Description: entity.Description || '',
      Owner: entity.Owner || '',
      Weight: entity.Weight || '',
      Deadline: entity.Deadline ? new Date(entity.Deadline).toISOString().split('T')[0] : '',
      IsCompleted: entity.IsCompleted || false
    });
  };

  const handleSave = async (entityId) => {
    try {
      const updateData = {};
      if (editData.Title) updateData.Title = editData.Title;
      if (editData.EntityType) updateData.EntityType = editData.EntityType;
      if (editData.TargetValue) updateData.TargetValue = parseFloat(editData.TargetValue);
      if (editData.TargetUnit) updateData.TargetUnit = editData.TargetUnit;
      if (editData.Description) updateData.Description = editData.Description;
      if (editData.Owner) updateData.Owner = editData.Owner;
      if (editData.Weight) updateData.Weight = parseFloat(editData.Weight);
      if (editData.Deadline) updateData.Deadline = editData.Deadline;
      updateData.IsCompleted = editData.IsCompleted;
      await updateEntity(entityId, updateData);
      setEditingId(null);
      setEditData({});
    } catch (error) {
      alert('Failed to update entity: ' + error.message);
    }
  };

  const handleDelete = async (entityId) => {
    if (window.confirm('Are you sure? This will delete the entity and all its signals.')) {
      try {
        await deleteEntity(entityId);
      } catch (error) {
        alert('Failed to delete entity');
      }
    }
  };

  const getEntityWithStatus = (entity) => {
    const enhanced = entitiesWithYTD?.find(e => e.Id === entity.Id);
    return enhanced || entity;
  };

  const getSignalDisplay = (entity) => {
    const signal = latestSignals.get(entity.Id);
    if (!signal) return { value: 'No data', status: 'unknown', timestamp: null, reason: null };
    let value = 'N/A';
    if (signal.SignalType === 'Value' || signal.SignalType === 'Score') {
      value = signal.NumericValue?.toFixed(2) || 'N/A';
    } else if (signal.SignalType === 'Status') {
      value = signal.Status || 'N/A';
    } else if (signal.SignalType === 'Boolean') {
      value = signal.BooleanValue ? 'Yes' : 'No';
    }
    return { value, status: signal.Status || 'unknown', timestamp: signal.Timestamp, reason: signal.Reason || null };
  };

  const getDeadlineStatus = (entity) => {
    const enhanced = getEntityWithStatus(entity);
    if (!enhanced.Deadline) return { text: null, class: '', daysRemaining: null };
    if (enhanced.deadlineStatus) {
      const ds = enhanced.deadlineStatus;
      return { text: ds.message, class: `deadline-${ds.statusColor}`, daysRemaining: ds.daysRemaining, status: ds.status };
    }
    const deadline = new Date(enhanced.Deadline);
    const now = new Date();
    const daysRemaining = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
    if (enhanced.IsCompleted) return { text: 'Completed', class: 'deadline-green', daysRemaining: 0, status: 'Completed' };
    if (daysRemaining < 0) return { text: `Overdue ${Math.abs(daysRemaining)}d`, class: 'deadline-red', daysRemaining, status: 'Delayed' };
    if (daysRemaining <= 7) return { text: `Due in ${daysRemaining}d`, class: 'deadline-yellow', daysRemaining, status: 'At Risk' };
    return { text: `${daysRemaining}d left`, class: 'deadline-blue', daysRemaining, status: 'On Track' };
  };

  const getStatusClass = (status) => {
    if (!status) return 'status-unknown';
    const s = status.toLowerCase();
    if (s.includes('not met') || s.includes('blocked')) return 'status-danger';
    if (s.includes('met') || s.includes('completed') || s.includes('on track')) return 'status-success';
    if (s.includes('at risk') || s.includes('delayed')) return 'status-warning';
    return 'status-unknown';
  };

  return (
    <div className="entity-grid">
      {entities.map(entity => {
        const signalData = getSignalDisplay(entity);
        const deadlineData = getDeadlineStatus(entity);
        const isEditing = editingId === entity.Id;
        const displayStatus = deadlineData?.status || signalData.status;

        if (isEditing) {
          return (
            <div key={entity.Id} className="entity-card editing">
              <div className="card-edit-grid">
                <div className="edit-row">
                  <label>Name</label>
                  <input
                    type="text"
                    value={editData.Title}
                    onChange={(e) => setEditData({ ...editData, Title: e.target.value })}
                    className="edit-input"
                    placeholder="Entity name"
                  />
                </div>
                <div className="edit-row">
                  <label>Type</label>
                  <select
                    value={editData.EntityType}
                    onChange={(e) => setEditData({ ...editData, EntityType: e.target.value })}
                    className="edit-input"
                  >
                    <option value="Metric">Metric</option>
                    <option value="Project">Project</option>
                    <option value="CSF">CSF</option>
                    <option value="Constraint">Constraint</option>
                    <option value="Initiative">Initiative</option>
                  </select>
                </div>
                <div className="edit-row">
                  <label>Description</label>
                  <textarea
                    value={editData.Description}
                    onChange={(e) => setEditData({ ...editData, Description: e.target.value })}
                    className="edit-input"
                    rows="2"
                    placeholder="Optional"
                  />
                </div>
                <div className="edit-row two-col">
                  <div>
                    <label>Target Value</label>
                    <input
                      type="number"
                      value={editData.TargetValue}
                      onChange={(e) => setEditData({ ...editData, TargetValue: e.target.value })}
                      className="edit-input"
                      placeholder="e.g. 100"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <label>Unit</label>
                    <input
                      type="text"
                      value={editData.TargetUnit}
                      onChange={(e) => setEditData({ ...editData, TargetUnit: e.target.value })}
                      className="edit-input"
                      placeholder="e.g. %"
                    />
                  </div>
                </div>
                <div className="edit-row two-col">
                  <div>
                    <label>Owner</label>
                    <input
                      type="text"
                      value={editData.Owner}
                      onChange={(e) => setEditData({ ...editData, Owner: e.target.value })}
                      className="edit-input"
                      placeholder="Owner name"
                    />
                  </div>
                  <div>
                    <label>Weight</label>
                    <input
                      type="number"
                      value={editData.Weight}
                      onChange={(e) => setEditData({ ...editData, Weight: e.target.value })}
                      className="edit-input"
                      placeholder="0–1"
                      step="0.01" min="0" max="1"
                    />
                  </div>
                </div>
                <div className="edit-row two-col">
                  <div>
                    <label>Deadline</label>
                    <input
                      type="date"
                      value={editData.Deadline}
                      onChange={(e) => setEditData({ ...editData, Deadline: e.target.value })}
                      className="edit-input"
                    />
                  </div>
                  <div className="completed-check">
                    <label>
                      <input
                        type="checkbox"
                        checked={editData.IsCompleted}
                        onChange={(e) => setEditData({ ...editData, IsCompleted: e.target.checked })}
                      />
                      <span>Completed</span>
                    </label>
                  </div>
                </div>
              </div>
              <div className="card-edit-actions">
                <button className="btn-save" onClick={() => handleSave(entity.Id)}>Save</button>
                <button className="btn-cancel" onClick={() => { setEditingId(null); setEditData({}); }}>Cancel</button>
              </div>
            </div>
          );
        }

        return (
          <div key={entity.Id} className="entity-card">
            <div className="card-header">
              <div className="card-title-row">
                <span className="card-title" title={entity.Description || ''}>{entity.Title}</span>
                <span className={`entity-type-badge type-${entity.EntityType?.toLowerCase()}`}>
                  {entity.EntityType}
                </span>
              </div>
              {entity.Description && (
                <div className="card-description">{entity.Description}</div>
              )}
            </div>

            <div className="card-body">
              <div className="card-stat">
                <span className="stat-label">Current</span>
                <span className="stat-value">{signalData.value}</span>
              </div>

              {entity.TargetValue && (
                <div className="card-stat">
                  <span className="stat-label">Target</span>
                  <span className="stat-value">{entity.TargetValue} {entity.TargetUnit || ''}</span>
                </div>
              )}


            </div>

            <div className="card-footer">
              <div className="card-badges">
                <span className={`status-badge ${getStatusClass(displayStatus)}`}>
                  {displayStatus}
                </span>
                {deadlineData.text && (
                  <span className={`deadline-badge ${deadlineData.class}`}>
                    {deadlineData.text}
                  </span>
                )}
              </div>

              <div className="card-meta">
                {entity.Owner && <span className="meta-owner" title="Owner">{entity.Owner}</span>}
                {signalData.timestamp && (
                  <span className="meta-timestamp">
                    {format(new Date(signalData.timestamp), 'MMM dd, HH:mm')}
                  </span>
                )}
              </div>

              {signalData.reason && (
                <div className="signal-reason" title={signalData.reason}>{signalData.reason}</div>
              )}
            </div>

            {canEdit && (
              <div className="card-actions">
                <button className="btn-icon btn-edit" onClick={() => handleEdit(entity)} title="Edit">
                  <svg viewBox="0 0 16 16" fill="currentColor"><path d="M11.013 1.427a1.75 1.75 0 012.474 0l1.086 1.086a1.75 1.75 0 010 2.474l-8.61 8.61c-.21.21-.47.364-.756.445l-3.251.93a.75.75 0 01-.927-.928l.929-3.25c.081-.286.235-.547.445-.758l8.61-8.61zm.176 4.823L9.75 4.81l-6.286 6.287a.253.253 0 00-.064.108l-.558 1.953 1.953-.558a.253.253 0 00.108-.064l6.286-6.286zm1.238-3.763a.25.25 0 00-.354 0L10.811 3.75l1.439 1.44 1.263-1.263a.25.25 0 000-.354l-1.086-1.086z"/></svg>
                </button>
                <button className="btn-icon btn-delete" onClick={() => handleDelete(entity.Id)} title="Delete">
                  <svg viewBox="0 0 16 16" fill="currentColor"><path d="M11 1.75V3h2.25a.75.75 0 010 1.5H2.75a.75.75 0 010-1.5H5V1.75C5 .784 5.784 0 6.75 0h2.5C10.216 0 11 .784 11 1.75zM4.496 6.675l.66 6.6a.25.25 0 00.249.225h5.19a.25.25 0 00.249-.225l.66-6.6a.75.75 0 011.492.149l-.66 6.6A1.748 1.748 0 0110.595 15h-5.19a1.75 1.75 0 01-1.741-1.575l-.66-6.6a.75.75 0 011.492-.15zM6.5 1.75V3h3V1.75a.25.25 0 00-.25-.25h-2.5a.25.25 0 00-.25.25z"/></svg>
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default DynamicEntityTable;
